/* Copyright (c) 2018-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Injectable } from '@angular/core';
import { Observable, Subscriber } from 'rxjs';
import { BridgeCommands } from 'bridge-commands/bridge.commands';
import { Logger } from '../logging/logging.service';
import { BridgeServiceInterface } from './bridge-service.interface';
import { BridgeMockService } from './bridge.mocks.service';

export interface FullBridgeCommand<ParamType extends object> {
    command: string;
    system: string;
    module: string;
    method: string;
    payload: ParamType;
    [key: string]: any; // Clone of payload. TODO: remove when cefclient handles the payload key for all queries
}

export interface BridgeError<ParamType extends object> {
    code: number;
    message: string;
    isCancelled: boolean;
    request: FullBridgeCommand<ParamType>;
}

@Injectable()
export abstract class BridgeService implements BridgeServiceInterface {
    constructor(
        protected logger: Logger,
        private bridgeMockService: BridgeMockService
    ) {}

    protected makeFullBridgeCommand<ParamType extends object, ResponseType>(
        command: BridgeCommands.Command<ParamType, ResponseType>,
        params: ParamType,
        includeTopLevelParams: boolean
    ): FullBridgeCommand<ParamType> {
        const obj: ParamType = includeTopLevelParams ? params : null;
        return {
            command: command.legacyCommand,
            system: command.system,
            module: command.module,
            method: command.method,
            payload: params,
            ...(obj as object)
        };
    }

    private unpackReturnPayload<ResponseType>(responseStr: string): ResponseType | string {
        try {
            const parsedResponse: any = JSON.parse(responseStr);
            const isMessageBusPayload =
                typeof parsedResponse === 'object' &&
                'reference_uniqueid' in parsedResponse &&
                'method' in parsedResponse &&
                'payload' in parsedResponse;
            if (isMessageBusPayload) {
                return parsedResponse.payload as ResponseType;
            } else {
                return parsedResponse as ResponseType;
            }
        } catch (err) {
            return responseStr;
        }
    }

    protected handleSuccess<ResponseType>(observer: Subscriber<any>, persistent: boolean, response: string) {
        switch (response) {
            case 'true':
                observer.next(true);
                break;
            case 'false':
                observer.next(false);
                break;
            default:
                observer.next(this.unpackReturnPayload<ResponseType>(response));
                break;
        }
        if (!persistent) {
            observer.complete();
        }
    }

    protected handleFailure<ParamType extends object>(
        observer: Subscriber<any>,
        request: FullBridgeCommand<ParamType>,
        errorCode: number,
        errorMessage: string,
        shouldLogPayload: boolean,
        shouldIncludePayloadInError: boolean = true
    ): void {
        const requestCopy = shouldIncludePayloadInError ? request : { ...request, payload: null };

        const error: BridgeError<ParamType> = {
            code: errorCode,
            message: errorMessage,
            isCancelled: errorCode === 204,
            request: requestCopy
        };

        this.logger.info(
            `Bridge Query returned error code ${errorCode}: ${errorMessage}. Command: ${JSON.stringify(
                shouldLogPayload ? request : request.command
            )}`
        );
        observer.error(error);
    }

    executeCommand<ParamType extends object, ResponseType>(
        command: BridgeCommands.Command<ParamType, ResponseType>,
        params?: ParamType,
        shouldLogPayload = true,
        shouldIncludePayloadInError: boolean = true
    ): Observable<ResponseType> {
        if (this.bridgeMockService.hasMock(command)) {
            return this.bridgeMockService.executeCommand(command, params);
        } else {
            return this.executeCommandInternal<ParamType, ResponseType>(
                command,
                params,
                shouldLogPayload,
                shouldIncludePayloadInError
            );
        }
    }

    protected abstract executeCommandInternal<ParamType extends object, ResponseType>(
        command: BridgeCommands.Command<ParamType, ResponseType>,
        params: ParamType,
        shouldLogPayload: boolean,
        shouldIncludePayloadInError: boolean
    ): Observable<ResponseType>;
}
